import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingReservationRequest, WarehousingStockUnit } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel, itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-reservation-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputNumberModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    ToastModule,
    PageDataPanelComponent,
    PageDetailHeroComponent,
    PageFormSectionComponent,
    PageMetricStripComponent,
    PageShellComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-reservation-view.component.html',
  styleUrl: './warehousing-reservation-view.component.css'
})
export class WarehousingReservationViewComponent implements OnInit {
  readonly itemIdentityLabel = itemIdentityLabel;
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private messageService = inject(MessageService);

  isLoading = false;
  isLoadingCandidates = false;
  isBulkReserving = false;
  request?: WarehousingReservationRequest | null;
  candidateUnits: WarehousingStockUnit[] = [];
  savingKeys: Record<string, boolean> = {};
  draftQuantities: Record<number, number> = {};
  returnToListQueryParams: Params = {};

  ngOnInit(): void {
    this.returnToListQueryParams = this.route.snapshot.queryParams;
    this.loadRequest();
  }

  loadRequest(): void {
    const requestId = Number(this.route.snapshot.paramMap.get('id'));
    if (!requestId) {
      this.request = null;
      return;
    }

    this.isLoading = true;
    forkJoin({
      request: this.warehousingService.getReservationRequest(requestId)
    }).subscribe({
      next: response => {
        this.request = response.request.data;
        this.isLoading = false;
      },
      error: () => {
        this.request = null;
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Reservation',
          detail: 'Unable to load the selected reservation request.'
        });
      }
    });
  }

  refreshCandidates(showMessage = false): void {
    if (this.request?.deletedAt || !this.request?.itemId || !this.request?.warehouseId || (this.request.remainingQuantity || 0) <= 0) {
      this.candidateUnits = [];
      return;
    }

    this.isLoadingCandidates = true;
    this.warehousingService.getStockUnits({
      itemId: this.request.itemId,
      warehouseId: this.request.warehouseId,
      conditionId: this.request.conditionId || null,
      status: 'AVAILABLE'
    }).subscribe({
      next: response => {
        this.candidateUnits = (response.data || []).filter(unit => (unit.availableQuantity || 0) > 0);
        this.candidateUnits.forEach(unit => this.ensureDraftQuantity(unit));
        this.isLoadingCandidates = false;
        if (showMessage) {
          this.messageService.add({
            severity: 'info',
            summary: 'Available Units',
            detail: 'Candidate stock units were refreshed.'
          });
        }
      },
      error: () => {
        this.isLoadingCandidates = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Available Units',
          detail: 'Unable to load candidate stock units.'
        });
      }
    });
  }

  reserveUnit(unit: WarehousingStockUnit): void {
    if (!this.request?.id || this.request.deletedAt || !unit.id) return;
    const quantity = this.draftQuantity(unit);
    if (!quantity || quantity <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Reserve Unit', detail: 'Enter a quantity greater than zero.' });
      return;
    }
    if (quantity > (this.request.remainingQuantity || 0)) {
      this.messageService.add({ severity: 'warn', summary: 'Reserve Unit', detail: 'Quantity exceeds the remaining request quantity.' });
      return;
    }
    if (quantity > (unit.availableQuantity || 0)) {
      this.messageService.add({ severity: 'warn', summary: 'Reserve Unit', detail: 'Quantity exceeds the stock unit availability.' });
      return;
    }

    const key = this.reserveKey(unit.id);
    if (this.isSaving(key) || this.isBulkReserving) return;
    this.savingKeys[key] = true;
    this.warehousingService.reserveReservationAllocation(this.request.id, {
      stockUnitId: unit.id,
      quantity
    }).subscribe({
      next: response => {
        this.savingKeys[key] = false;
        if (!response.data) return;
        this.request = response.data;
        this.refreshCandidates();
        this.messageService.add({
          severity: 'success',
          summary: 'Reserved',
          detail: 'Stock unit reserved against the warehouse request.'
        });
      },
      error: error => {
        this.savingKeys[key] = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Reserve Failed',
          detail: error?.error?.message || error?.error || 'Unable to reserve the selected stock unit.'
        });
      }
    });
  }

  reserveAllUnits(): void {
    if (!this.request?.id || this.request.deletedAt || this.isBulkReserving) return;
    const candidateIds = this.reserveAllCandidateIds();
    if (!candidateIds.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Reserve All',
        detail: 'Load candidate stock units first, then reserve them in bulk.'
      });
      return;
    }

    const beforeRemaining = this.request.remainingQuantity || 0;
    this.isBulkReserving = true;
    this.warehousingService.reserveAllReservationAllocations(this.request.id, {
      stockUnitIds: candidateIds
    }).subscribe({
      next: response => {
        this.isBulkReserving = false;
        if (!response.data) return;
        this.request = response.data;
        this.refreshCandidates();
        const afterRemaining = this.request.remainingQuantity || 0;
        const reservedNow = Math.max(beforeRemaining - afterRemaining, 0);
        this.messageService.add({
          severity: 'success',
          summary: 'Bulk Reserved',
          detail: afterRemaining > 0
            ? `${this.formatQuantity(reservedNow)} reserved from the loaded candidates. ${this.formatQuantity(afterRemaining)} still remains.`
            : 'Loaded candidate stock units fully covered the request.'
        });
      },
      error: error => {
        this.isBulkReserving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Reserve All Failed',
          detail: error?.error?.message || error?.error || 'Unable to bulk reserve the loaded stock units.'
        });
      }
    });
  }

  releaseReservation(allocationId?: number): void {
    if (!this.request?.id || this.request.deletedAt || !allocationId) return;
    const key = this.releaseKey(allocationId);
    if (this.isSaving(key) || this.isBulkReserving) return;
    this.savingKeys[key] = true;
    this.warehousingService.releaseReservationAllocation(this.request.id, allocationId).subscribe({
      next: response => {
        this.savingKeys[key] = false;
        if (!response.data) return;
        this.request = response.data;
        this.refreshCandidates();
        this.messageService.add({
          severity: 'success',
          summary: 'Released',
          detail: 'Stock unit allocation was released.'
        });
      },
      error: error => {
        this.savingKeys[key] = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Release Failed',
          detail: error?.error?.message || error?.error || 'Unable to release the allocation.'
        });
      }
    });
  }

  metrics(): PageMetric[] {
    return [
      { label: 'Requested', value: this.request?.requestedQuantity || 0 },
      { label: 'Reserved', value: this.request?.reservedQuantity || 0, tone: 'warn' },
      { label: 'Remaining', value: this.request?.remainingQuantity || 0, tone: (this.request?.remainingQuantity || 0) > 0 ? 'info' : 'success' },
      { label: 'Allocations', value: this.request?.allocations?.length || 0, tone: 'success' }
    ];
  }

  reservationSeverity(status?: string): 'success' | 'secondary' | 'warn' | 'info' | 'danger' {
    const value = `${status || ''}`.toUpperCase();
    if (value === 'RESERVED') return 'success';
    if (value === 'PARTIAL') return 'warn';
    if (value === 'PLANNED') return 'secondary';
    return 'info';
  }

  canViewOrigin(): boolean {
    return this.request?.referenceType === 'POSM_PROJECT_ITEM_SOURCE' && !!this.request.referenceHeaderId;
  }

  referenceTypeLabel(): string {
    return this.request?.referenceType === 'POSM_PROJECT_ITEM_SOURCE'
      ? 'POSM Project'
      : (this.request?.referenceType || 'Reservation Source');
  }

  displayDate(value?: string | Date): string {
    if (!value) return '-';
    return value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  }

  displayDateTime(value?: string | Date): string {
    if (!value) return '-';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }).format(date);
  }

  itemSizeLabel(): string {
    return itemSizeLabel(this.request);
  }

  itemWeightLabel(): string {
    return this.request?.weightKg === null || this.request?.weightKg === undefined
      ? 'Not recorded'
      : `${this.formatDimension(this.request.weightKg)} kg`;
  }

  itemVolumeLabel(): string {
    return this.request?.cbm === null || this.request?.cbm === undefined
      ? 'Not recorded'
      : `${this.formatDimension(this.request.cbm)} CBM`;
  }

  reservationTime(): string | Date | undefined {
    const allocationTimes = (this.request?.allocations || [])
      .map(allocation => allocation.createdAt)
      .filter((value): value is string => !!value)
      .sort((left, right) => this.timestamp(left) - this.timestamp(right));
    return allocationTimes[0] || this.request?.createdAt;
  }

  reservationTimeLabel(): string {
    return (this.request?.allocations || []).some(allocation => !!allocation.createdAt)
      ? 'First Stock Allocation'
      : 'Reservation Request Created';
  }

  candidateCoverageText(): string {
    const remaining = this.request?.remainingQuantity || 0;
    const candidateCount = this.reserveAllCandidateIds().length;
    if (!remaining) {
      return 'This request is already fully covered.';
    }
    if (!candidateCount) {
      return 'Load candidates when you want to reserve units against the remaining quantity.';
    }
    const coverable = Math.min(this.loadedCandidateAvailableQuantity(), remaining);
    return coverable >= remaining
      ? `${candidateCount} loaded units can fully cover the remaining ${this.formatQuantity(remaining)}.`
      : `${candidateCount} loaded units can cover ${this.formatQuantity(coverable)} of ${this.formatQuantity(remaining)} remaining.`;
  }

  draftQuantity(unit: WarehousingStockUnit): number {
    return this.draftQuantities[unit.id] ?? this.defaultDraftQuantity(unit);
  }

  setDraftQuantity(stockUnitId: number, quantity: number | null | undefined): void {
    this.draftQuantities[stockUnitId] = Number(quantity) || 0;
  }

  maxReservableQuantity(unit: WarehousingStockUnit): number {
    return Math.min(this.request?.remainingQuantity || 0, unit.availableQuantity || 0);
  }

  isSaving(key: string): boolean {
    return !!this.savingKeys[key];
  }

  canReserveAllLoadedUnits(): boolean {
    return !this.request?.deletedAt
      && !this.isBulkReserving
      && !this.isLoadingCandidates
      && (this.request?.remainingQuantity || 0) > 0
      && this.reserveAllCandidateIds().length > 0;
  }

  private ensureDraftQuantity(unit: WarehousingStockUnit): void {
    if (this.draftQuantities[unit.id] !== undefined) return;
    this.draftQuantities[unit.id] = this.defaultDraftQuantity(unit);
  }

  private defaultDraftQuantity(unit: WarehousingStockUnit): number {
    return Math.max(Math.min(this.request?.remainingQuantity || 0, unit.availableQuantity || 0), 0);
  }

  private reserveKey(stockUnitId: number): string {
    return `reserve:${stockUnitId}`;
  }

  private releaseKey(allocationId: number): string {
    return `release:${allocationId}`;
  }

  private reserveAllCandidateIds(): number[] {
    return this.candidateUnits
      .filter(unit => (unit.availableQuantity || 0) > 0)
      .map(unit => unit.id)
      .filter((id): id is number => !!id);
  }

  private loadedCandidateAvailableQuantity(): number {
    return this.candidateUnits.reduce((sum, unit) => sum + (unit.availableQuantity || 0), 0);
  }

  private formatQuantity(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4
    }).format(value || 0);
  }

  private formatDimension(value?: number): string {
    return value === null || value === undefined
      ? '-'
      : new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
  }

  private timestamp(value: string | Date): number {
    const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
  }
}
